import { logger } from '../../../utils/logger.js';

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);

    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display: inline-block;
    font-family: var(--ctl-font);
    vertical-align: middle;
  }

  .btn{
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--ctl-space-1-5);
    cursor: pointer;
    user-select: none;
    
    padding: var(--padding, var(--ctl-space-2) var(--ctl-space-3));
    border-radius: var(--radius, var(--ctl-radius-10));
    border: var(--ctl-border-width-thin) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    
    font-size: var(--font-size, var(--ctl-font-size-md));
    font-weight: var(--ctl-font-weight-semibold);
    letter-spacing: var(--ctl-tracking-normal);
    line-height: 1;
    
    transition: all var(--ctl-transition-normal);
  }

  .btn:hover{
    background: var(--ctl-color-hover);
    border-color: color-mix(in srgb, var(--ctl-color-border) 80%, var(--ctl-color-text));
  }

  .btn:active{
    background: var(--ctl-color-active);
  }

  .btn:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .btn:disabled{
    opacity: var(--ctl-opacity-disabled);
    cursor: not-allowed;
    pointer-events: none;
  }

  /* success state (after copy) */
  .btn.success{
    background: color-mix(in srgb, var(--ctl-color-success) 12%, var(--ctl-color-surface));
    border-color: color-mix(in srgb, var(--ctl-color-success) 25%, var(--ctl-color-border));
    color: var(--ctl-color-success);
  }

  .icon{
    display: inline-flex;
    width: var(--ctl-size-icon-sm);
    height: var(--ctl-size-icon-sm);
    flex-shrink: 0;
  }

  .label{
    display: inline-flex;
  }

  .label:empty{
    display: none;
  }

  /* sizes */
  :host([size="sm"]){
    --padding: var(--ctl-space-1-5) var(--ctl-space-2-5);
    --radius: var(--ctl-radius-md);
    --font-size: var(--ctl-font-size-sm);
  }

  :host([size="sm"]) .icon{
    width: var(--ctl-space-3-5);
    height: var(--ctl-space-3-5);
  }

  :host(:not([size]), [size="md"]){
    --padding: var(--ctl-space-2) var(--ctl-space-3);
    --radius: var(--ctl-radius-10);
    --font-size: var(--ctl-font-size-md);
  }

  :host([size="lg"]){
    --padding: var(--ctl-space-2-5) var(--ctl-space-3-5);
    --radius: var(--ctl-radius-12);
    --font-size: var(--ctl-font-size-base);
  }

  :host([size="lg"]) .icon{
    width: var(--ctl-font-size-xl);
    height: var(--ctl-font-size-xl);
  }

  /* icon-only variant */
  :host([icon-only]) .btn{
    padding: var(--padding, var(--ctl-space-2));
    width: var(--size, var(--ctl-space-6) + var(--ctl-space-1));
    height: var(--size, var(--ctl-space-6) + var(--ctl-space-1));
  }

  :host([icon-only][size="sm"]) .btn{
    --size: var(--ctl-space-6);
  }

  :host([icon-only][size="lg"]) .btn{
    --size: calc(var(--ctl-space-6) + var(--ctl-space-2));
  }
</style>

<button id="btn" class="btn" part="button" type="button">
  <span class="icon" part="icon">
    <svg id="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
    <svg id="check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:none;">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  </span>
  <span class="label" part="label">
    <slot></slot>
  </span>
</button>
`;

export class CtlCopyButton extends HTMLElement {
  static get observedAttributes() {
    return ["value", "size", "icon-only", "disabled"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._btn = this.shadowRoot.getElementById("btn");
    this._copyIcon = this.shadowRoot.getElementById("copy-icon");
    this._checkIcon = this.shadowRoot.getElementById("check-icon");
    this._onClick = this._onClick.bind(this);
    this._resetTimeout = null;
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    
    this._btn.addEventListener("click", this._onClick);
    
    // Watch for slot changes to update aria-label
    const labelSlot = this.shadowRoot.querySelector('slot');
    if (labelSlot) {
      this._labelSlot = labelSlot;
      this._slotChangeHandler = () => this._updateAriaLabel();
      labelSlot.addEventListener('slotchange', this._slotChangeHandler);
    }
    
    this._sync();
    this._updateAriaLabel();
  }

  disconnectedCallback() {
    this._btn.removeEventListener("click", this._onClick);
    if (this._labelSlot && this._slotChangeHandler) {
      this._labelSlot.removeEventListener('slotchange', this._slotChangeHandler);
    }
    if (this._resetTimeout) {
      clearTimeout(this._resetTimeout);
    }
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get value() { return this.getAttribute("value") || ""; }
  set value(v) { v ? this.setAttribute("value", String(v)) : this.removeAttribute("value"); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get iconOnly() { return this.hasAttribute("icon-only"); }
  set iconOnly(v) { v ? this.setAttribute("icon-only", "") : this.removeAttribute("icon-only"); }

  get disabled() { return this.hasAttribute("disabled"); }
  set disabled(v) { v ? this.setAttribute("disabled", "") : this.removeAttribute("disabled"); }

  _sync() {
    const size = this.size;
    if (!["sm", "md", "lg"].includes(size)) {
      this.setAttribute("size", "md");
    }

    this._btn.disabled = this.disabled;
  }
  
  _updateAriaLabel() {
    // Check if there's text content in the slot
    const labelSlot = this.shadowRoot.querySelector('slot:not([name])');
    const hasTextContent = labelSlot && labelSlot.assignedNodes().some(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent.trim().length > 0;
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        return node.textContent.trim().length > 0;
      }
      return false;
    });
    
    // If no text content or icon-only mode, ensure aria-label exists
    if (!hasTextContent || this.iconOnly) {
      // Use explicit aria-label if provided
      if (this.hasAttribute('aria-label')) {
        this._btn.setAttribute('aria-label', this.getAttribute('aria-label'));
      } else {
        // Generate descriptive label from value or use default
        const value = this.value;
        if (value) {
          this._btn.setAttribute('aria-label', `Copy ${value}`);
        } else {
          this._btn.setAttribute('aria-label', 'Copy to clipboard');
        }
      }
    } else {
      // Has text content, use it for the label
      const textContent = this.textContent.trim();
      this._btn.setAttribute('aria-label', textContent || 'Copy');
    }
  }

  async _onClick(e) {
    e.preventDefault();
    if (this.disabled) return;

    const value = this.value;
    if (!value) {
      logger.warn("ctl-copy-button: no value to copy");
      return;
    }

    try {
      // Try modern clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(value);
        this._showSuccess();
      } else {
        // Fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const success = document.execCommand("copy");
        document.body.removeChild(textarea);
        
        if (success) {
          this._showSuccess();
        } else {
          throw new Error("Copy command failed");
        }
      }

      // Emit success event
      this.dispatchEvent(new CustomEvent("ctl-copy", {
        bubbles: true,
        composed: true,
        detail: { value }
      }));
    } catch (err) {
      logger.trackError("ctl-copy-button", err, { value });
      
      // Emit error event
      this.dispatchEvent(new CustomEvent("ctl-copy-error", {
        bubbles: true,
        composed: true,
        detail: { error: err, value }
      }));
    }
  }

  _showSuccess() {
    // Switch to check icon
    this._copyIcon.style.display = "none";
    this._checkIcon.style.display = "block";
    this._btn.classList.add("success");
    
    // Update aria-label to announce success
    const originalLabel = this._btn.getAttribute('aria-label');
    this._btn.setAttribute('aria-label', 'Copied!');

    // Reset after delay
    if (this._resetTimeout) {
      clearTimeout(this._resetTimeout);
    }
    
    this._resetTimeout = setTimeout(() => {
      this._copyIcon.style.display = "block";
      this._checkIcon.style.display = "none";
      this._btn.classList.remove("success");
      
      // Restore original aria-label
      if (originalLabel) {
        this._btn.setAttribute('aria-label', originalLabel);
      }
      
      this._resetTimeout = null;
    }, 2000);
  }
}

customElements.define("ctl-copy-button", CtlCopyButton);
